import { createSelector } from '@reduxjs/toolkit';
import { clearSortAndFilter, setSortAndFilter } from 'component/page/search/store/search';
import {
    EmptyResultInfo,
    selectFilterState,
    selectMatrixFromSearchState,
    selectResultInfoFromSearchState,
    selectSearchStateIsLoading,
    selectSortState,
} from 'component/page/search/store/search.selectors';
import { Pagination } from 'gql/autogen/Pagination';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { SortMatrix } from 'gql/autogen/SortMatrix';
import { FilterMatrix } from 'gql/autogen/FilterMatrix';
import { Sort } from 'gql/autogen/Sort';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesCategoriesFilterClickEventActivitiesFieldsV6,
    ActivitiesSortOptionClickEventActivitiesFieldsV6,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesCategoriesFilterClickEventActivitiesManifestV6,
    ActivitiesSortOptionClickEventActivitiesManifestV6,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { FilterState } from 'component/common/Filter/Filter.types';
import { selectIsGlobalKeywordSearchPage } from 'store/slice/navigation';

export enum SortAndFilterContentVariant {
    Sidebar = 'sidebar',
    Overlay = 'overlay',
    CategoryBottomSheet = 'categoryBottomSheet',
}

export interface OwnProps {
    visible: boolean;
    variant: SortAndFilterContentVariant;
    onFilterStateChange?: (filterState: FilterState) => void;
    onSortStateChange?: (sortState: Sort) => void;
}

interface StateProps {
    cms: CmsValue;
    sort: Sort;
    filter: FilterState;
    sortMatrix: SortMatrix;
    filterMatrix: FilterMatrix;
    page: Pagination;
    isLoading: boolean;
    isGlobalKeywordSearchPage: boolean;
    isFilterWithoutUpdatingHistory: boolean;
}

interface DispatchProps {
    setSortAndFilter: (locationSearch: string, page: Pagination, sort: Sort, filter: FilterState) => void;
    clearSortAndFilter: (locationSearch: string) => void;
    onCategoryFilterChange: (categoryValue: string, order_index: number, activities_count: number) => void;
    onSortSelectionChange: (sort: Sort, index: number) => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectSearchStateIsLoading,
    selectResultInfoFromSearchState,
    selectSortState,
    selectFilterState,
    selectMatrixFromSearchState,
    selectIsGlobalKeywordSearchPage,
    selectFeatureBehaviorFromBootstrap,
    (cms, isLoading, resultInfo, sort, filter, matrix, isGlobalKeywordSearchPage, features): StateProps => {
        const sortMatrix = matrix?.sort || {};
        const filterMatrix = matrix?.filter || {};
        const page = resultInfo.pagination || EmptyResultInfo.pagination;
        return {
            cms,
            sort,
            filter,
            page,
            sortMatrix,
            filterMatrix,
            isLoading,
            isGlobalKeywordSearchPage: features.GlobalKeywordSearch && isGlobalKeywordSearchPage,
            isFilterWithoutUpdatingHistory: features.FilterWithoutUpdatingHistory,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function, props: OwnProps): DispatchProps => {
    return {
        setSortAndFilter: (locationSearch: string, page: Pagination, sort: Sort, filter: FilterState) => {
            dispatch(setSortAndFilter({ sort, filter }));
        },
        clearSortAndFilter: () => {
            dispatch(clearSortAndFilter());
        },
        onCategoryFilterChange: (categoryValue: string, order_index: number, activities_count: number) => {
            trackElementEvent<ActivitiesCategoriesFilterClickEventActivitiesFieldsV6>(
                ActivitiesCategoriesFilterClickEventActivitiesManifestV6.ids,
                {
                    is_categories_tab: false,
                    order_index,
                    activities_count,
                    is_sidebar: props.variant === SortAndFilterContentVariant.Sidebar,
                    is_filter_bottom_sheet: props.variant === SortAndFilterContentVariant.CategoryBottomSheet,
                },
                { action_element_value: categoryValue, action_element_index: order_index === -1 ? 1 : order_index + 1 },
            );
        },
        onSortSelectionChange: (sort: Sort, index: number) => {
            trackElementEvent<ActivitiesSortOptionClickEventActivitiesFieldsV6>(
                ActivitiesSortOptionClickEventActivitiesManifestV6.ids,
                {
                    is_sidebar: props.variant === SortAndFilterContentVariant.Sidebar,
                },
                { action_element_value: `${sort.code}_${sort.order}`, action_element_index: index + 1 },
            );
        },
    };
};
